import { useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ImportFailureIndicatorsButtonProps {
  sessionId: string;
  workspaceId: string;
  indicators: string[];
}

export function ImportFailureIndicatorsButton({
  sessionId,
  workspaceId,
  indicators,
}: ImportFailureIndicatorsButtonProps) {
  const [isImporting, setIsImporting] = useState(false);
  const [isImported, setIsImported] = useState(false);
  const { toast } = useToast();

  const handleImport = async () => {
    if (indicators.length === 0) return;
    setIsImporting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Nicht eingeloggt");

      const { data: existing } = await supabase
        .from("failure_indicators")
        .select("indicator")
        .eq("session_id", sessionId);

      const existingSet = new Set(existing?.map(e => e.indicator) || []);
      const newIndicators = indicators.filter(i => !existingSet.has(i));

      if (newIndicators.length === 0) {
        toast({ title: "Bereits importiert", description: "Alle Failure Indicators wurden bereits hinzugefügt." });
        setIsImported(true);
        return;
      }

      const inserts = newIndicators.map(indicator => ({
        session_id: sessionId,
        workspace_id: workspaceId,
        indicator,
        source: "report",
        created_by: user.id,
      }));

      const { error } = await supabase.from("failure_indicators").insert(inserts);
      if (error) throw error;

      toast({ title: "Failure Indicators importiert", description: `${newIndicators.length} Indikatoren zum Monitoring hinzugefügt.` });
      setIsImported(true);
    } catch (error: any) {
      toast({ title: "Fehler beim Import", description: error.message, variant: "destructive" });
    } finally {
      setIsImporting(false);
    }
  };

  if (indicators.length === 0) return null;

  return (
    <Button
      variant={isImported ? "outline" : "secondary"}
      size="sm"
      onClick={handleImport}
      disabled={isImporting || isImported}
      className="gap-2"
    >
      {isImporting ? (
        <><Loader2 className="h-4 w-4 animate-spin" />Importiere...</>
      ) : isImported ? (
        <><Check className="h-4 w-4 text-green-600" />Zum Monitoring hinzugefügt</>
      ) : (
        <><AlertTriangle className="h-4 w-4" />Failure Indicators tracken</>
      )}
    </Button>
  );
}
